/**
 * leadAttribution.js — connects a website visit to the outreach email it came from,
 * ONLY when the visitor has accepted Analytics cookies.
 *
 * How it fits together:
 *   • client/public/lead-token.js runs first in <head>: it reads ?sl=<code> from an
 *     outreach link into memory (window.__sfLeadToken) and removes it from the URL.
 *   • This module decides what happens next:
 *       – no Analytics consent  → nothing is stored and nothing is sent; the code
 *         lives only in this tab's memory and is gone when the tab closes.
 *       – Analytics consent     → the code is kept for 90 days (localStorage
 *         "sf_lead_v1"), the landing and each page path are sent to
 *         POST /api/leads/track, and a signed-in account is linked once via
 *         POST /api/leads/link.
 *       – consent given later in the same visit → the current page is sent then.
 *       – consent withdrawn     → the stored code is deleted immediately.
 *   • Only the page PATH is sent (never the query string). Failures are silent:
 *     attribution must never interrupt the visitor.
 *
 * Declared to visitors in client/src/config/consentCategories.json (Analytics).
 */
import api from './api';

export const STORAGE_KEY = 'sf_lead_v1';
const SESSION_KEY = 'sf_lead_session';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;

let initialised = false;
let token = null;
let sessionId = null;
let landingSent = false;
let lastSentPath = null;
let pendingPath = null;
const linkedUsers = new Set();

const hasAnalyticsConsent = () => {
  try {
    return Boolean(window.__consent && window.__consent.get() && window.__consent.get().analytics === true);
  } catch {
    return false;
  }
};

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { t, at } = JSON.parse(raw);
    if (!TOKEN_RE.test(t) || !at || Date.now() - at > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return t;
  } catch {
    return null;
  }
};

const persist = (t) => {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ t, at: Date.now() })); } catch { /* ignore */ }
};

const forget = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch { /* ignore */ }
};

const getSessionId = () => {
  if (sessionId) return sessionId;
  try {
    const stored = window.sessionStorage.getItem(SESSION_KEY);
    if (stored && /^[a-z0-9]{8,32}$/i.test(stored)) {
      sessionId = stored;
      landingSent = true; // same tab, same visit — the landing was already recorded
      return sessionId;
    }
  } catch { /* ignore */ }
  sessionId = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
  sessionId = sessionId.replace(/[^a-z0-9]/gi, '').slice(0, 32);
  try { window.sessionStorage.setItem(SESSION_KEY, sessionId); } catch { /* ignore */ }
  return sessionId;
};

const cleanPath = (p) => (String(p || '/').split('?')[0].split('#')[0].slice(0, 300) || '/');

// Fire-and-forget, and safe even if the request itself throws synchronously.
const quietPost = (url, body) => Promise.resolve()
  .then(() => api.post(url, body))
  .then(() => true, () => false);

const send = (type, path) => {
  quietPost('/leads/track', { token, type, path, sessionId: getSessionId() });
};

const track = (path) => {
  if (!token || !hasAnalyticsConsent()) return;
  const p = cleanPath(path);
  if (!landingSent) {
    getSessionId();
    if (!landingSent) {
      landingSent = true;
      lastSentPath = p;
      send('landing', p);
      return;
    }
  }
  if (p === lastSentPath) return;
  lastSentPath = p;
  send('page_view', p);
};

const onConsentChange = (state) => {
  if (state && state.analytics) {
    if (!token) token = readStored();
    if (token) {
      persist(token);
      if (pendingPath) {
        const p = pendingPath;
        pendingPath = null;
        track(p);
      }
    }
  } else {
    forget();
    pendingPath = null;
  }
};

/** Call once at start-up. Safe to call again (no-op). */
export const initLeadAttribution = () => {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  const fromLink = typeof window.__sfLeadToken === 'string' && TOKEN_RE.test(window.__sfLeadToken)
    ? window.__sfLeadToken : null;
  if (hasAnalyticsConsent()) {
    token = fromLink || readStored();
    if (fromLink) persist(fromLink);
  } else {
    token = fromLink; // memory only until consent
  }
  try {
    if (window.__consent && typeof window.__consent.subscribe === 'function') {
      window.__consent.subscribe(onConsentChange);
    }
  } catch { /* ignore */ }
};

/** Call on every route change with the new pathname. */
export const onLeadRouteChange = (pathname) => {
  initLeadAttribution();
  if (!token) return;
  if (!hasAnalyticsConsent()) {
    pendingPath = pathname; // sent only if they accept Analytics during this visit
    return;
  }
  track(pathname);
};

/** Call when the signed-in user changes; links the account once per user per visit. */
export const linkLeadToUser = (userId) => {
  initLeadAttribution();
  if (!userId || !token || !hasAnalyticsConsent() || linkedUsers.has(userId)) return;
  linkedUsers.add(userId);
  quietPost('/leads/link', { token }).then((ok) => { if (!ok) linkedUsers.delete(userId); });
};

/** Test helper. */
export const __resetLeadAttribution = () => {
  initialised = false;
  token = null;
  sessionId = null;
  landingSent = false;
  lastSentPath = null;
  pendingPath = null;
  linkedUsers.clear();
};
